import { TrendingUp, Layers, Workflow, BrainCircuit, Target } from "lucide-react";
import { motion } from "framer-motion";
import { OUTCOMES, TONES, IMAGES } from "@/data/content";
import { Reveal } from "@/components/Reveal";
import { Eyebrow, SectionTitle } from "@/components/Section";
import { MiniBars, Sparkline } from "@/components/Decor";

const ICONS = { trending: TrendingUp, layers: Layers, workflow: Workflow, brain: BrainCircuit, target: Target };

const Visual = ({ tone, icon }) => {
  const t = TONES[tone];
  if (icon === "trending") return <MiniBars color={t.accent} className="!h-16" heights={[30, 45, 40, 62, 58, 80, 74, 96]} />;
  if (icon === "workflow")
    return (
      <div className="flex items-center gap-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg border" style={{ borderColor: t.accent, background: i % 2 ? t.tint : "#fff" }} />
            {i < 3 && <span className="h-px w-4" style={{ background: t.accent }} />}
          </div>
        ))}
      </div>
    );
  if (icon === "target") return <Sparkline color={t.accent} className="!h-14" />;
  if (icon === "layers")
    return (
      <div className="relative h-14 w-24">
        {[0, 1, 2].map((i) => (
          <span key={i} className="absolute left-0 right-0 h-8 rounded-xl border bg-white" style={{ top: i * 10, left: i * 8, right: -i * 8, borderColor: `${t.accent}55`, zIndex: 3 - i }} />
        ))}
      </div>
    );
  return (
    <div className="flex gap-1.5">
      {[...Array(12)].map((_, i) => (
        <span key={i} className="h-2 w-2 rounded-full" style={{ background: i % 3 === 0 ? t.accent : `${t.accent}33` }} />
      ))}
    </div>
  );
};

export const Outcomes = () => (
  <section className="relative py-24 lg:py-32 overflow-hidden" data-testid="outcomes-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
        <div className="max-w-2xl">
          <Eyebrow>Τι Κερδίζετε</Eyebrow>
          <SectionTitle className="mt-6">
            Τι αλλάζει πρακτικά στην <span className="italic text-[#C26D52]">επιχείρησή σας</span>
          </SectionTitle>
        </div>
        <p className="max-w-md text-base leading-relaxed text-[#3B4A5E]">
          Δεν παραδίδουμε παρουσιάσεις που μένουν στο συρτάρι. Δουλεύουμε μαζί σας ώστε η στρατηγική να
          γίνεται καθημερινή λειτουργία — και τα αποτελέσματα να φαίνονται.
        </p>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
        {OUTCOMES.map((o, i) => {
          const Icon = ICONS[o.icon];
          const t = TONES[o.tone];
          const dark = o.tone === "navy";
          return (
            <Reveal key={o.title} delay={i * 0.06} className={`${o.span} ${i === 0 ? "md:col-span-2 lg:col-span-7" : ""}`}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`outcome-card-${i}`}
                className={`relative h-full overflow-hidden rounded-[28px] p-8 lg:p-10 border ${dark ? "mesh-navy text-white border-white/10" : "bg-white border-[#0E1B31]/[0.07]"} shadow-[0_24px_60px_-40px_rgba(14,27,49,0.35)]`}
              >
                {i === 0 && (
                  <img src={IMAGES.warehouse} alt="" className="pointer-events-none absolute right-0 top-0 h-full w-1/2 object-cover opacity-[0.12] [mask-image:linear-gradient(to_left,black,transparent)]" loading="lazy" />
                )}
                <div className="relative flex items-start justify-between gap-6">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: dark ? "rgba(255,255,255,0.1)" : t.tint, color: dark ? "#fff" : t.accent }}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <Visual tone={dark ? "bluegrey" : o.tone} icon={o.icon} />
                </div>
                <h3 className={`relative mt-8 font-display text-2xl lg:text-3xl font-semibold ${dark ? "text-white" : "text-[#0E1B31]"}`}>{o.title}</h3>
                <p className={`relative mt-3 text-sm lg:text-[15px] leading-relaxed max-w-md ${dark ? "text-white/75" : "text-[#3B4A5E]"}`}>{o.desc}</p>
              </motion.div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
